import { z } from "zod";

import { paginationMetaSchema } from "./base-api-response";
import { paginationSchema } from "./pagination";
import { EServiceCategory, EServiceType } from "./common";
import { userBasicPublicInfoSchema, basicCustomerDetailsSchema } from "./user";
import { partnerBasicDetailsSchema } from "./partner";
import {
  ESessionAssignmentRequestType,
  ESessionAssignmentRequestScope,
  ESessionAssignmentRequestStatus,
  ESessionAssignmentRequestAttemptStatus,
  ESessionAssignmentRequestTypeScope,
  ENotificationChannel,
  ENotificationAttemptStatus,
  EProcessStage,
  ESessionAssignmentRequestCycleStatus,
} from "../constants";

export enum ERequestStatus {
  IN_PROGRESS = "In Progress",
  CANCELLED = "Cancelled",
  ASSIGNED = "Assigned",
  PENDING = "Pending",
  COMPLETED = "Completed",
}

// Note: Use ICycleDetail and IAttemptDetail types inferred from Zod schemas above
// They match the backend schema exactly, no need for duplicate interfaces

export interface ISubscriptionInfo {
  city: string;
  subcity: string;
  hub: string;
  category: string;
  plan: string;
  requestType?: string;
  dateOfSession?: string;
  numberOfSessions?: number;
  currentSessionTime?: string;
  requestedSessionTime?: string;
}

export interface ISessionRequest {
  id: string;
  customer: string;
  customerId?: string;
  category: EServiceCategory;
  serviceType: EServiceType;
  requestType: ESessionAssignmentRequestTypeScope;
  requestTypeUrl?: string;
  location: string;
  created: number;
  scheduled: number;
  status: ESessionAssignmentRequestStatus;
  summary: {
    partnersCount: number;
    attempts: number;
    duration?: string;
  };
  requestCycle?: ICycleDetail;
  cycles?: ICycleDetail[];
  subscriptionInfo?: ISubscriptionInfo;
}
export interface ISessionRequestFilterOption {
  id: string;
  label: string;
  default?: boolean;
  icon?: React.ReactNode;
}

export interface IGenericFilters {
  value: string;
  label: string;
}

/**
 * Session assignment request summary schema
 */
export const sessionAssignmentRequestSummarySchema = z.object({
  partnerCount: z.number().int().min(0),
  attemptCount: z.number().int().min(0),
  totalTimeMs: z.number().int().min(0),
});

/**
 * Session assignment request list item response schema
 */
export const sessionAssignmentRequestListItemResponseSchema = z.object({
  id: z.string().uuid(),
  user: userBasicPublicInfoSchema,
  subscriptionId: z.string().uuid().nullable(),
  sessionIds: z.array(z.string().uuid()),
  serviceCategory: z.nativeEnum(EServiceCategory),
  serviceType: z.nativeEnum(EServiceType),
  type: z.nativeEnum(ESessionAssignmentRequestType),
  scope: z.nativeEnum(ESessionAssignmentRequestScope),
  city: z.string(),
  createdAt: z.number().int(),
  scheduled: z.number().int().nullable(),
  status: z.nativeEnum(ESessionAssignmentRequestStatus),
  summary: sessionAssignmentRequestSummarySchema,
});

/**
 * Session assignment request list response schema
 */
export const sessionAssignmentRequestListResponseSchema = z.object({
  items: z.array(sessionAssignmentRequestListItemResponseSchema),
  meta: paginationMetaSchema,
});

/**
 * Notification attempt schema
 */
export const notificationAttemptSchema = z.object({
  channel: z.nativeEnum(ENotificationChannel),
  status: z.nativeEnum(ENotificationAttemptStatus),
  attemptNumber: z.number().int().min(1),
  error: z.string().nullish(),
  sentAt: z.number().int(),
});

/**
 * Attempt detail schema
 */
export const attemptDetailSchema = z.object({
  id: z.string().uuid(),
  partner: partnerBasicDetailsSchema.nullish(),
  numberOfSessions: z.number().int().min(0),
  sentAt: z.number().int().nullish(),
  expiresAt: z.number().int().nullish(),
  status: z.nativeEnum(ESessionAssignmentRequestAttemptStatus),
  distance: z.string().optional(), // Optional field for distance display
  rankingScore: z.number().nullish(),
  rankingFactors: z.record(z.unknown()).nullish(),
  computedParameters: z.record(z.unknown()).nullish(),
  notificationAttempts: z.array(notificationAttemptSchema).default([]),
  processStage: z.nativeEnum(EProcessStage).nullish(),
  failureMessage: z.string().nullish(),
  isSystemFailureEntry: z.boolean().default(false),
});

/**
 * Cycle detail schema
 */
export const cycleDetailSchema = z.object({
  id: z.string().uuid(),
  cycleNumber: z.number().int().min(1),
  status: z.nativeEnum(ESessionAssignmentRequestCycleStatus),
  startedAt: z.number().int(),
  endedAt: z.number().int().nullish(),
  numberOfAttempts: z.number().int().min(0),
  totalSessions: z.number().int().min(0),
  failureReason: z.string().nullish(),
  outcomeMessage: z.string().nullish(),
  attempts: z.array(attemptDetailSchema),
});

/**
 * Session assignment request detail response schema
 */
export const sessionAssignmentRequestDetailResponseSchema = z.object({
  id: z.string().uuid(),
  type: z.nativeEnum(ESessionAssignmentRequestType),
  scope: z.nativeEnum(ESessionAssignmentRequestScope),
  subscriptionId: z.string().uuid().nullish(),
  sessionIds: z.array(z.string().uuid()),
  status: z.nativeEnum(ESessionAssignmentRequestStatus),
  serviceCategory: z.nativeEnum(EServiceCategory),
  serviceType: z.nativeEnum(EServiceType),
  customer: basicCustomerDetailsSchema,
  cycles: z.array(cycleDetailSchema),
});

/**
 * Get session assignment requests query schema
 */
export const getSessionAssignmentRequestsQuerySchema = paginationSchema.extend({
  status: z.nativeEnum(ESessionAssignmentRequestStatus).nullable().optional(),
  serviceCategory: z.nativeEnum(EServiceCategory).nullable().optional(),
  serviceType: z.nativeEnum(EServiceType).nullable().optional(),
  requestType: z
    .nativeEnum(ESessionAssignmentRequestType)
    .nullable()
    .optional(),
  requestScope: z
    .nativeEnum(ESessionAssignmentRequestScope)
    .nullable()
    .optional(),
  userIds: z.array(z.string()).nullable().optional(),
  partnerIds: z.array(z.string().uuid()).nullable().optional(),
  subscriptionId: z.string().uuid().nullish(),
  sessionId: z.string().uuid().nullish(),
  search: z.string().optional(),
  createdAtFrom: z.preprocess(
    (val) => (val ? Number(val) : undefined),
    z.number().int().positive().nullish()
  ),
  createdAtTo: z.preprocess(
    (val) => (val ? Number(val) : undefined),
    z.number().int().positive().nullish()
  ),
  cityCode: z.string().trim().min(1).nullish(),
  subCityCode: z.string().trim().min(1).nullish(),
  hubCode: z.string().trim().min(1).nullish(),
  hotspotCode: z.string().trim().min(1).nullish(),
});

// Type exports
export type ISessionAssignmentRequestSummary = z.infer<
  typeof sessionAssignmentRequestSummarySchema
>;
export type ISessionAssignmentRequestListItem = z.infer<
  typeof sessionAssignmentRequestListItemResponseSchema
>;
export type ISessionAssignmentRequestListResponse = z.infer<
  typeof sessionAssignmentRequestListResponseSchema
>;
export type INotificationAttempt = z.infer<typeof notificationAttemptSchema>;
export type IAttemptDetail = z.infer<typeof attemptDetailSchema>;
export type ICycleDetail = z.infer<typeof cycleDetailSchema>;
export type ISessionAssignmentRequestDetail = z.infer<
  typeof sessionAssignmentRequestDetailResponseSchema
>;
export type IGetSessionAssignmentRequestsQuery = z.infer<
  typeof getSessionAssignmentRequestsQuerySchema
>;
